import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  // Check if it's an admin endpoint
  const isAdminEndpoint = config.url?.includes('/admin/') && !config.url?.includes('/admin/login');
  const token = isAdminEndpoint 
    ? localStorage.getItem('metag_admin_token')
    : localStorage.getItem('metag_token');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAdminEndpoint = error.config?.url?.includes('/admin/');
    if (error.response?.status === 401 && !isAdminEndpoint) {
      localStorage.removeItem('metag_token');
      localStorage.removeItem('metag_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Analytics APIs
export const analyticsAPI = {
  track: (slug, eventType, extra = {}) => {
    // Fire-and-forget — never throw, never block UI
    api.post('/analytics/track', {
      slug, eventType,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      ...extra
    }).catch(() => {});
  },
  get: (slug) => api.get(`/analytics/${slug}`),
  getSnapshot: (slug) => api.get(`/analytics/${slug}/snapshot`),
  getEvents: (slug, page = 1, perPage = 20, eventType = null) => {
    const params = new URLSearchParams({ page, per_page: perPage });
    if (eventType) params.append('event_type', eventType);
    return api.get(`/analytics/${slug}/events?${params}`);
  },
};

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Order APIs
export const orderAPI = {
  create: (data) => api.post('/orders/create', data),
  verify: (data) => api.post('/orders/verify', data),
  getAll: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
};

// Card APIs
export const cardAPI = {
  getAll: () => api.get('/cards'),
  updateUrl: (data) => api.put('/update-url', data),
  getRedirect: (code) => api.get(`/redirect/${code}`),
  deleteCard: (cardCode) => api.delete(`/cards/${cardCode}`),
  updateDesign: (urlCode, design) => api.put(`/cards/${urlCode}/design`, { design }),
};

// Settings APIs
export const settingsAPI = {
  updateName: (name) => api.put('/auth/settings/name', { name }),
  changePassword: (current_password, new_password) => api.put('/auth/settings/password', { current_password, new_password }),
};

// Referral APIs
export const referralAPI = {
  getMyReferrals: () => api.get('/auth/referrals/me'),
};

// Support APIs
export const supportAPI = {
  getTickets: () => api.get('/support/tickets'),
  createTicket: (data) => api.post('/support/tickets', data),
};

// Profile APIs
export const profileAPI = {
  get: () => api.get('/profile'),
  create: (data) => api.post('/profile', data),
  delete: () => api.delete('/profile'),
  checkSlug: (slug) => api.get(`/profile/check-slug/${slug}`),
  getPublic: (slug) => api.get(`/p/${slug}`),
  getVCard: (slug) => `${API}/p/${slug}/vcard`,
};

// Admin APIs
export const adminAPI = {
  login: (data) => api.post('/admin/login', data),
  getDashboard: () => api.get('/admin/dashboard'),
  getOrders: () => api.get('/admin/orders'),
  updateShipping: (orderId, data) => api.put(`/admin/orders/${orderId}/shipping`, data),
  deleteOrder: (orderId) => api.delete(`/admin/orders/${orderId}`),
  sendInvoice: (orderId) => api.post(`/admin/orders/${orderId}/send-invoice`),
  getUsers: () => api.get('/admin/users'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getCards: () => api.get('/admin/cards'),
  createCard: (data) => api.post('/admin/cards/create', data),
  updateCard: (cardId, data) => api.put(`/admin/cards/${cardId}`, data),
  deleteCard: (cardId) => api.delete(`/admin/cards/${cardId}`),
  getProfiles: () => api.get('/admin/profiles'),
  getTickets: () => api.get('/admin/tickets'),
  exportDb: () => api.get('/admin/export-db', { responseType: 'blob' }),
};

// Upload APIs
export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (filename) => api.delete(`/upload/${filename}`),
};

// Public APIs
export const publicAPI = {
  getRazorpayKey: () => api.get('/razorpay-key'),
};

export default api;
